import { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { CaretLeft, CaretRight, Phone } from '@phosphor-icons/react';
import { apptStatusColors } from './agendaConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// URS-F235: mini calendar lateral con cadencia de 5 minutos para alinear con
// la vista principal (DayView usa DAY_SLOT_MINUTES=5). 14 horas (07:00-20:55)
// × 12 slots/hora = 168 rows. Cell height pequeño para que quepa en el panel.
const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7:00 - 20:00
const SLOT_MIN = 5;
const SLOT_H = 6; // px por slot de 5 min -> 60 min = 72 px, 14h = 1008 px (scroll)
const SLOTS_PER_HOUR = 60 / SLOT_MIN; // 12

const statusLabels = {
  citada: 'Citada', anulada: 'Anulada', no_asiste: 'No asiste',
  sala_espera: 'Sala espera', especialista: 'Especialista',
  sondas: 'Sondas', terminada: 'Terminada',
};

function pad2(n) { return String(n).padStart(2, '0'); }

function fmtDateStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

const DAYS_ES = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export function MiniDayView({ date, appointments: parentAppts, studies, scheduleSlots, scheduleBlocks, activeStudyId, onSelectTime, selectedTime, lang }) {
  const [viewDate, setViewDate] = useState(() => date ? new Date(date + 'T00:00:00') : new Date());
  const [fetchedAppts, setFetchedAppts] = useState([]);
  const scrollRef = useRef(null);

  // URS-F236: "now line" para la franja horaria actual, solo cuando viewDate
  // coincide con hoy. Se actualiza cada minuto igual que DayView.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Sync when parent date changes
  useEffect(() => {
    if (date) {
      const nd = new Date(date + 'T00:00:00');
      if (!isNaN(nd.getTime())) {
        setViewDate(nd);
      }
    }
  }, [date]);

  const dayStr = fmtDateStr(viewDate);

  // Load appointments for the visible day from API when not in parent data
  useEffect(() => {
    axios.get(`${API_URL}/api/appointments?date_from=${dayStr}&date_to=${dayStr}`, { withCredentials: true })
      .then(r => setFetchedAppts(r.data || []))
      .catch(() => setFetchedAppts([]));
  }, [dayStr]);

  const studyMap = useMemo(() => {
    const m = {};
    for (const s of studies || []) m[s.study_id] = s;
    return m;
  }, [studies]);

  // Programacion (schedule slots) del dia. Si hay activeStudyId pintamos solo los
  // slots de ese estudio; si no hay, pintamos todos en gris muy tenue.
  const daySlotBands = useMemo(() => {
    if (!scheduleSlots || scheduleSlots.length === 0) return [];
    const filtered = scheduleSlots.filter(s => s.date === dayStr);
    return filtered.map(s => {
      const [sh, sm] = (s.start_time || '00:00').split(':').map(Number);
      const [eh, em] = (s.end_time || s.start_time || '00:00').split(':').map(Number);
      const startMin = (sh - 7) * 60 + sm;
      const endMin = (eh - 7) * 60 + em;
      if (endMin <= 0 || startMin >= (HOURS.length * 60)) return null;
      const top = (Math.max(startMin, 0) / SLOT_MIN) * SLOT_H;
      const height = Math.max(((Math.min(endMin, HOURS.length * 60) - Math.max(startMin, 0)) / SLOT_MIN) * SLOT_H, 4);
      const study = studyMap[s.study_id];
      const color = study?.color || '#6B7280';
      const isActive = !activeStudyId || s.study_id === activeStudyId;
      return {
        slot_id: s.slot_id || `${s.study_id}-${s.date}-${s.start_time}`,
        top, height, color, isActive,
        label: study ? study.code : (s.study_id || ''),
      };
    }).filter(Boolean);
  }, [scheduleSlots, dayStr, studyMap, activeStudyId]);

  // Bloqueos (schedule_blocks) del dia: globales (study_id null) SIEMPRE +
  // los del estudio activo si lo hay. URS-F252.
  const dayBlockBands = useMemo(() => {
    if (!scheduleBlocks || scheduleBlocks.length === 0) return [];
    return scheduleBlocks
      .filter(b => b.date === dayStr && (!b.study_id || b.study_id === activeStudyId))
      .map(b => {
        const [sh, sm] = (b.start_time || '00:00').split(':').map(Number);
        const [eh, em] = (b.end_time || b.start_time || '00:00').split(':').map(Number);
        const startMin = (sh - 7) * 60 + sm;
        const endMin = (eh - 7) * 60 + em;
        if (endMin <= 0 || startMin >= (HOURS.length * 60)) return null;
        const top = (Math.max(startMin, 0) / SLOT_MIN) * SLOT_H;
        const height = Math.max(((Math.min(endMin, HOURS.length * 60) - Math.max(startMin, 0)) / SLOT_MIN) * SLOT_H, 4);
        return { block_id: b.block_id, top, height, reason: b.reason || '', is_global: !b.study_id };
      }).filter(Boolean);
  }, [scheduleBlocks, activeStudyId, dayStr]);

  const dayAppts = useMemo(() => {
    const allAppts = [...(parentAppts || []), ...fetchedAppts];
    // Dedupe by appointment_id
    const seen = new Set();
    const unique = allAppts.filter(a => {
      if (seen.has(a.appointment_id)) return false;
      seen.add(a.appointment_id);
      return a.date === dayStr;
    });
    return unique.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }, [parentAppts, fetchedAppts, dayStr]);

  const nav = (dir) => {
    const nd = new Date(viewDate);
    nd.setDate(nd.getDate() + dir);
    setViewDate(nd);
  };

  const dayLabel = `${DAYS_ES[viewDate.getDay()]} ${viewDate.getDate()} ${MONTHS_ES[viewDate.getMonth()]}`;

  // Build appointment positions. Multiple citas en el mismo bucket de hora se
  // reparten en columnas dentro del area visible (left:42 -> right:4) usando
  // el mismo algoritmo que DayView (study_id+visit_code -> study_id -> nueva).
  const apptPositions = useMemo(() => {
    const byTime = {};
    dayAppts.forEach(a => {
      const k = a.time || '08:00';
      if (!byTime[k]) byTime[k] = [];
      byTime[k].push(a);
    });
    const positionByAppt = {};
    Object.keys(byTime).forEach(timeKey => {
      const tg = byTime[timeKey];
      const total = Math.max(1, tg.length);
      tg.forEach((appt, idx) => {
        positionByAppt[appt.appointment_id] = { col: idx, totalCols: total };
      });
    });
    return dayAppts.map(a => {
      if (!a.time) return null;
      const [h, m] = a.time.split(':').map(Number);
      const minFrom7 = (h - 7) * 60 + m;
      const top = (minFrom7 / SLOT_MIN) * SLOT_H;
      const dur = a.duration_minutes || 15;
      const height = Math.max((dur / SLOT_MIN) * SLOT_H, SLOT_H * 2);
      const study = studyMap[a.study_id];
      const color = study?.color || (!a.study_id || a.visit_code === 'Qsalud' ? '#059669' : '#6B7280');
      const pos = positionByAppt[a.appointment_id] || { col: 0, totalCols: 1 };
      return { ...a, top, height, color, study, col: pos.col, totalCols: pos.totalCols };
    }).filter(Boolean);
  }, [dayAppts, studyMap]);

  const handleSlotClick = (hour, minute) => {
    const time = `${pad2(hour)}:${pad2(minute)}`;
    onSelectTime && onSelectTime(dayStr, time);
  };

  // URS-F236: la franja roja "now" solo se pinta cuando el dia visible es hoy
  // y la hora actual cae dentro del rango (07:00 - 21:00).
  const today = new Date();
  const isToday = today.getFullYear() === viewDate.getFullYear()
    && today.getMonth() === viewDate.getMonth()
    && today.getDate() === viewDate.getDate();
  const nowMinFrom7 = now.getHours() * 60 + now.getMinutes() - 7 * 60;
  const totalMin = HOURS.length * 60;
  const showNow = isToday && nowMinFrom7 >= 0 && nowMinFrom7 <= totalMin;
  const nowTop = (nowMinFrom7 / SLOT_MIN) * SLOT_H;
  const nowLabel = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;

  // URS-F236: al abrir el dialog (o cambiar al dia de hoy), centrar el scroll
  // en la franja horaria actual para que el operador no tenga que buscarla.
  useEffect(() => {
    if (!scrollRef.current || !showNow) return;
    const container = scrollRef.current;
    const target = Math.max(0, nowTop - container.clientHeight / 2);
    container.scrollTop = target;
    // Solo en el primer render del dayStr; no en cada tick de "now".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayStr]);

  return (
    <div className="flex flex-col h-full" data-testid="mini-day-view">
      {/* Header navigation */}
      <div className="flex items-center justify-between px-2 py-2 border-b border-stone-200 bg-stone-50/50">
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); e.preventDefault(); nav(-1); }} data-testid="mini-nav-prev">
          <CaretLeft size={12} />
        </Button>
        <span className="text-xs font-semibold text-stone-700">{dayLabel}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6 mr-8" onClick={(e) => { e.stopPropagation(); e.preventDefault(); nav(1); }} data-testid="mini-nav-next">
          <CaretRight size={12} />
        </Button>
      </div>

      {/* Timeline */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden" style={{ maxHeight: 420 }}>
        <div className="relative" style={{ height: HOURS.length * SLOTS_PER_HOUR * SLOT_H }}>
          {/* Slot rows (5-min cadence). Solo la fila :00 muestra la etiqueta de
              hora; :15/:30/:45 obtienen un border ligeramente mas oscuro para
              orientacion visual; el resto son lineas muy tenues. */}
          {HOURS.map(hour => (
            Array.from({ length: SLOTS_PER_HOUR }, (_, i) => {
              const minute = i * SLOT_MIN;
              const isHour = minute === 0;
              const isQuarter = minute === 15 || minute === 30 || minute === 45;
              const borderClass = isHour
                ? 'border-t border-stone-300'
                : (isQuarter ? 'border-t border-stone-200' : 'border-t border-stone-100');
              return (
                <div
                  key={`${hour}-${minute}`}
                  className={`flex items-start cursor-pointer hover:bg-blue-50/40 transition-colors ${borderClass}`}
                  style={{ height: SLOT_H }}
                  onClick={() => handleSlotClick(hour, minute)}
                  data-testid={`mini-slot-${pad2(hour)}-${pad2(minute)}`}
                >
                  {isHour && (
                    <span className="text-[10px] text-stone-500 w-10 text-right pr-1.5 shrink-0 font-mono font-semibold leading-none" style={{ marginTop: -3 }}>{pad2(hour)}:00</span>
                  )}
                  {!isHour && (
                    <span className="w-10 shrink-0" />
                  )}
                  <div className="flex-1 h-full" />
                </div>
              );
            })
          ))}

          {/* Programacion del estudio (bandas de slots en color tenue) */}
          {daySlotBands.map(b => (
            <div
              key={`slot-${b.slot_id}`}
              className="absolute pointer-events-none rounded-sm"
              style={{
                top: b.top,
                height: b.height,
                left: 42,
                right: 4,
                backgroundColor: b.color,
                opacity: b.isActive ? 0.18 : 0.08,
                border: `1px dashed ${b.color}`,
                zIndex: 1,
              }}
              data-testid={`mini-slot-band-${b.slot_id}`}
              title={b.label}
            />
          ))}

          {/* Bloqueos (schedule_blocks) del estudio activo */}
          {dayBlockBands.map(b => (
            <div
              key={`block-${b.block_id}`}
              className="absolute pointer-events-none rounded-sm"
              style={{
                top: b.top,
                height: b.height,
                left: 42,
                right: 4,
                backgroundColor: '#dc2626',
                opacity: 0.22,
                backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 4px, rgba(255,255,255,0.45) 4px 8px)',
                border: '1px solid #dc2626',
                zIndex: 3,
              }}
              data-testid={`mini-block-${b.block_id}`}
              title={b.reason ? `Bloqueo: ${b.reason}` : 'Bloqueado'}
            />
          ))}

          {/* Appointment blocks (column-aware para evitar solapamientos) */}
          {apptPositions.map(a => {
            const sc = apptStatusColors[a.status];
            const widthPct = 100 / (a.totalCols || 1);
            return (
              <div
                key={a.appointment_id}
                className="absolute rounded-sm px-1.5 py-0.5 text-white text-[10px] leading-tight overflow-hidden shadow-sm border border-white/20"
                style={{
                  top: a.top,
                  height: a.height,
                  left: `calc(42px + (100% - 46px) * ${a.col * widthPct} / 100)`,
                  width: `calc((100% - 46px) * ${widthPct} / 100 - 2px)`,
                  backgroundColor: a.color,
                  opacity: 0.92,
                  zIndex: 2,
                }}
                data-testid={`mini-appt-${a.appointment_id}`}
                title={`${a.time} | ${a.volunteer_name || a.study_volunteer_code || ''} | ${a.visit_code || ''}`}
              >
                <div className="font-semibold truncate">{a.time} {a.study_volunteer_code || a.volunteer_name || ''}</div>
                {a.height > SLOT_H * 4 && (
                  <div className="truncate opacity-80 flex items-center gap-1">{a.visit_code === 'QSALUD TFNO' && <Phone size={10} weight="bold" className="shrink-0" />}{a.visit_code} · {statusLabels[a.status] || a.status}</div>
                )}
              </div>
            );
          })}

          {/* URS-F236: franja "now" en rojo cuando el dia visible es hoy */}
          {showNow && (
            <div
              data-testid="mini-now-line"
              className="absolute left-10 right-1 pointer-events-none"
              style={{ top: nowTop - 1, zIndex: 15 }}
            >
              <div className="relative h-0.5 bg-red-500/90">
                <span
                  className="absolute -left-12 -top-2 inline-flex items-center px-1 py-0.5 rounded-sm bg-red-500 text-white text-[9px] font-mono font-semibold leading-none shadow-sm"
                >
                  {nowLabel}
                </span>
                <span className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
              </div>
            </div>
          )}

          {/* Selected time indicator (cadencia 5 min) */}
          {selectedTime && dayStr === (date || '') && (() => {
            const [sh, sm] = selectedTime.split(':').map(Number);
            const selTop = ((sh - 7) * 60 + sm) / SLOT_MIN * SLOT_H;
            return (
              <div
                className="absolute left-10 right-1 h-0.5 bg-[#1f3e80] z-20 pointer-events-none"
                style={{ top: selTop }}
              >
                <div className="absolute -left-1 -top-1.5 w-3 h-3 rounded-full bg-[#1f3e80] border-2 border-white" />
              </div>
            );
          })()}
        </div>
      </div>

      {/* Footer summary */}
      <div className="px-2 py-1.5 border-t border-stone-200 bg-stone-50/50">
        <span className="text-[10px] text-stone-500">
          {dayAppts.length} {lang === 'es' ? 'citas este dia' : 'appointments this day'}
        </span>
      </div>
    </div>
  );
}
